import {
  Component,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { useAuth } from "@/hooks/use-auth";
import { getUserSettings, type User } from "@/lib/api";
import {
  isRecoverableChunkLoadError,
  shouldAutoReloadForChunkError,
} from "@/lib/chunk-recovery";
import {
  isAuthFlowCompleteLocation,
  notifyAuthFlowComplete,
  subscribeAuthFlowComplete,
} from "@/lib/auth-flow";
import { cachePreferences, DEFAULT_PREFERENCES, type UserPreferences } from "@/lib/preferences";
import {
  foregroundRoute,
  formatAppRoute,
  isSettingsRoute,
  parseAppRoute,
  routeKey,
  type AppRoute,
  type SettingsSection,
} from "@/lib/app-route";
import {
  establishMiniAppAuthSession,
  getTelegramWebAppStartParam,
  useTelegramWebAppInitData,
  useTelegramMiniAppNoZoom,
} from "@/lib/telegram-miniapp";
import { attachBrowserAcquisition } from "@/lib/acquisition";
import {
  captureAuthenticatedPageview,
  resetPostHogUser,
  syncAuthenticatedPostHogUser,
} from "@/lib/posthog";

const AuthPage = lazy(() => import("@/pages/auth-page").then((m) => ({ default: m.AuthPage })));
const AppPage = lazy(() => import("@/pages/app-page").then((m) => ({ default: m.AppPage })));
const OnboardingPage = lazy(() =>
  import("@/pages/onboarding-page").then((m) => ({ default: m.OnboardingPage })),
);
const OnboardingSurveyPage = lazy(() =>
  import("@/pages/onboarding-survey-page").then((m) => ({ default: m.OnboardingSurveyPage })),
);
const SettingsPage = lazy(() =>
  import("@/pages/settings-page").then((m) => ({ default: m.SettingsPage })),
);
const TelegramSessionPage = lazy(() =>
  import("@/pages/telegram-session-page").then((m) => ({ default: m.TelegramSessionPage })),
);
const TelegramServerPage = lazy(() =>
  import("@/pages/telegram-server-page").then((m) => ({ default: m.TelegramServerPage })),
);
const TelegramFilePreviewPane = lazy(() =>
  import("@/pages/telegram-file-preview-page").then((m) => ({ default: m.TelegramFilePreviewPane })),
);
const TelegramManagerPage = lazy(() =>
  import("@/pages/telegram-manager-page").then((m) => ({ default: m.TelegramManagerPage })),
);

type Overlay = null;
const AUTH_REFRESH_MIN_INTERVAL_MS = 30_000;
const AUTH_REFRESH_INTERVAL_MS = 60_000;

export function App() {
  const auth = useAuth();
  const isAuthFlowComplete = isAuthFlowCompleteLocation();
  // OAuth/link redirects currently return to "/" rather than restoring an
  // arbitrary in-app path. Deep links are therefore only guaranteed for
  // already-authenticated users in this first routing pass.
  const telegramManagerRoute = getTelegramManagerRoute(window.location);
  const telegramFilePreviewRoute = getTelegramFilePreviewRoute(window.location);
  const telegramServerRoute = getTelegramServerRoute(window.location);
  const telegramSessionRoute = getTelegramSessionRoute(window.location);
  // Telegram Mini Apps are entered from Telegram-specific flows after web onboarding.
  // Keep future /tg/* routes outside the web-app survey gate as well.
  const isTelegramMiniAppRoute = window.location.pathname.startsWith("/tg");
  const shouldDisableTelegramMiniAppZoom = Boolean(
    isTelegramMiniAppRoute && !telegramFilePreviewRoute,
  );
  const [overlay, setOverlay] = useState<Overlay>(null);
  const [currentRoute, setCurrentRoute] = useState<AppRoute>(() => parseAppRoute(window.location.pathname));
  const [sidebarRefreshSignal, setSidebarRefreshSignal] = useState(0);
  const [selectedVpsIdHint, setSelectedVpsIdHint] = useState<string | null>(null);
  const [onboardingManualVpsId, setOnboardingManualVpsId] = useState<string | null>(null);
  const [linkedProviders, setLinkedProviders] = useState<string[]>([]);
  const [telegramStatus, setTelegramStatus] = useState<"linked" | "connected" | null>(null);
  const [telegramUsername, setTelegramUsername] = useState<string | null>(null);
  const [telegramDisplayName, setTelegramDisplayName] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [deferredSurveyUserId, setDeferredSurveyUserId] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const lastAuthRefreshRef = useRef(0);
  const foregroundRouteRef = useRef<AppRoute>({ kind: "root" });
  const routeSyncPendingRef = useRef(false);
  const settingsOpen = isSettingsRoute(currentRoute);
  const currentRouteRef = useRef(currentRoute);

  useTelegramMiniAppNoZoom(shouldDisableTelegramMiniAppZoom);
  const overlayRefState = useRef<Overlay>(overlay);
  const authenticatedUserId = auth.status === "authenticated" ? auth.user.user_id : null;
  const surveyDeferredForCurrentUser = Boolean(
    authenticatedUserId && deferredSurveyUserId === authenticatedUserId,
  );
  const settingsFocusVpsId = currentRoute.kind === "vpsSettings" ? currentRoute.vpsId : null;
  const settingsSection = currentRoute.kind === "settings" ? currentRoute.section : "plan";
  const newServerMode =
    currentRoute.kind === "newServer" ? currentRoute.mode : "choose";
  const effectiveOverlay =
    overlay ?? (currentRoute.kind === "newServer" ? "onboarding" : settingsOpen ? "settings" : null);
  const routeTarget = useMemo(
    () =>
      currentRoute.kind === "vps" || currentRoute.kind === "vpsSettings" || currentRoute.kind === "session"
        ? {
            key: routeKey(currentRoute),
            vpsId: currentRoute.vpsId,
            sessionId: currentRoute.kind === "session" ? currentRoute.sessionId : null,
          }
        : null,
    [currentRoute],
  );

  const syncRoute = useCallback((nextRoute: AppRoute, opts?: { history?: "push" | "replace"; state?: Record<string, unknown> }) => {
    const nextPath = formatAppRoute(nextRoute);
    setCurrentRoute(nextRoute);
    if (window.location.pathname === nextPath) {
      return;
    }
    const historyState = {
      ...(typeof window.history.state === "object" && window.history.state ? window.history.state as Record<string, unknown> : {}),
      ...(opts?.state ?? {}),
    };
    if ((opts?.history ?? "push") === "replace") {
      window.history.replaceState(historyState, "", nextPath);
      return;
    }
    window.history.pushState(historyState, "", nextPath);
  }, []);

  const handleNavigate = useCallback((target: "settings" | "onboarding", opts?: { manualVpsId?: string | null; vpsId?: string | null }) => {
    if (target === "onboarding") {
      setOnboardingManualVpsId(opts?.manualVpsId ?? null);
      syncRoute(
        { kind: "newServer", mode: opts?.manualVpsId ? "byo" : "choose" },
        {
          history: "push",
          state: {
            chatcodeOverlay: true,
            chatcodeOverlayBasePath: formatAppRoute(foregroundRouteRef.current),
          },
        },
      );
      return;
    }

    setOnboardingManualVpsId(null);
    syncRoute(
      opts?.vpsId ? { kind: "vpsSettings", vpsId: opts.vpsId } : { kind: "settings", section: "plan" },
      {
        history: "push",
        state: {
          chatcodeOverlay: true,
          chatcodeOverlayBasePath: formatAppRoute(foregroundRouteRef.current),
        },
      },
    );
  }, [syncRoute]);

  const handleSurveyComplete = useCallback(() => {
    void auth.checkAuth();
  }, [auth.checkAuth]);

  const handleSurveyDefer = useCallback(() => {
    if (authenticatedUserId) {
      setDeferredSurveyUserId(authenticatedUserId);
    }
  }, [authenticatedUserId]);

  const handleCloseOverlay = useCallback(() => {
    if (currentRoute.kind === "newServer") {
      setOnboardingManualVpsId(null);
      setOverlay(null);
      if (window.history.state?.chatcodeOverlay) {
        window.history.back();
        return;
      }
      syncRoute(foregroundRouteRef.current, { history: "replace" });
      return;
    }
    if (!settingsOpen) {
      return;
    }
    syncRoute(foregroundRouteRef.current, { history: "replace" });
  }, [currentRoute, settingsOpen, syncRoute]);

  const handleOnboardingComplete = useCallback((vpsId: string) => {
    setSelectedVpsIdHint(vpsId);
    setSidebarRefreshSignal((value) => value + 1);
    setOnboardingManualVpsId(null);
    setOverlay(null);
    if (currentRoute.kind === "newServer") {
      syncRoute(foregroundRouteRef.current, { history: "replace" });
    }
    window.setTimeout(() => setSelectedVpsIdHint(null), 0);
  }, [currentRoute, syncRoute]);

  const handleForegroundRouteChange = useCallback((foreground: { vpsId: string | null; sessionId: string | null }) => {
    const nextRoute = foregroundRoute(foreground.vpsId, foreground.sessionId);
    foregroundRouteRef.current = nextRoute;
    if (
      overlayRefState.current !== null ||
      currentRouteRef.current.kind === "newServer" ||
      isSettingsRoute(currentRouteRef.current) ||
      routeSyncPendingRef.current
    ) {
      return;
    }
    if (routeKey(currentRouteRef.current) === routeKey(nextRoute)) {
      return;
    }
    syncRoute(nextRoute);
  }, [syncRoute]);

  const handleSettingsSectionChange = useCallback((section: SettingsSection) => {
    syncRoute(
      { kind: "settings", section },
      {
        history: "replace",
        state: {
          chatcodeOverlay: true,
          chatcodeOverlayBasePath: formatAppRoute(foregroundRouteRef.current),
        },
      },
    );
  }, [syncRoute]);

  const handleRouteTargetHandled = useCallback((result: { kind: "applied" } | { kind: "missingVps" } | { kind: "missingSession"; vpsId: string }) => {
    routeSyncPendingRef.current = false;
    if (result.kind === "applied") {
      return;
    }
    if (result.kind === "missingVps") {
      syncRoute({ kind: "root" }, { history: "replace" });
      return;
    }
    syncRoute({ kind: "vps", vpsId: result.vpsId }, { history: "replace" });
  }, [syncRoute]);

  useEffect(() => {
    routeSyncPendingRef.current = routeTarget !== null;
  }, [routeTarget]);

  useEffect(() => {
    if (currentRoute.kind !== "settings") return;
    const canonicalPath = formatAppRoute(currentRoute);
    if (window.location.pathname === canonicalPath) return;
    window.history.replaceState(
      {
        ...(typeof window.history.state === "object" && window.history.state ? window.history.state as Record<string, unknown> : {}),
      },
      "",
      `${canonicalPath}${window.location.search}${window.location.hash}`,
    );
  }, [currentRoute]);

  useEffect(() => {
    currentRouteRef.current = currentRoute;
    overlayRefState.current = overlay;
  }, [currentRoute, overlay]);

  useEffect(() => {
    if (auth.status !== "authenticated") {
      setDeferredSurveyUserId(null);
      resetPostHogUser();
    }
  }, [auth.status, authenticatedUserId]);

  const handleNewServerModeChange = useCallback((mode: "choose" | "do" | "byo") => {
    syncRoute(
      { kind: "newServer", mode },
      {
        history: "push",
        state: {
          chatcodeOverlay: true,
          chatcodeOverlayBasePath: formatAppRoute(foregroundRouteRef.current),
        },
      },
    );
  }, [syncRoute]);

  useEffect(() => {
    if (!effectiveOverlay) return;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    overlayRef.current?.focus();
  }, [effectiveOverlay]);

  useEffect(() => {
    if (!effectiveOverlay) return;

    const { body, documentElement } = document;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyOverscroll = body.style.overscrollBehavior;
    const prevDocOverflow = documentElement.style.overflow;
    const prevDocOverscroll = documentElement.style.overscrollBehavior;

    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    documentElement.style.overflow = "hidden";
    documentElement.style.overscrollBehavior = "none";

    return () => {
      body.style.overflow = prevBodyOverflow;
      body.style.overscrollBehavior = prevBodyOverscroll;
      documentElement.style.overflow = prevDocOverflow;
      documentElement.style.overscrollBehavior = prevDocOverscroll;
    };
  }, [effectiveOverlay]);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentRoute(parseAppRoute(window.location.pathname));
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (auth.status !== "authenticated") {
      setLinkedProviders([]);
      setTelegramStatus(null);
      setTelegramUsername(null);
      setTelegramDisplayName(null);
      return;
    }
    setLinkedProviders(auth.user.providers ?? []);
    setTelegramStatus(auth.user.telegram?.status ?? null);
    setTelegramUsername(auth.user.telegram?.username ?? null);
    setTelegramDisplayName(auth.user.telegram?.display_name ?? null);
  }, [
    auth.status,
    auth.status === "authenticated" ? auth.user.providers : null,
    auth.status === "authenticated" ? auth.user.telegram?.status : null,
    auth.status === "authenticated" ? auth.user.telegram?.username : null,
    auth.status === "authenticated" ? auth.user.telegram?.display_name : null,
  ]);

  useEffect(() => {
    if (linkedProviders.includes("telegram")) return;
    setTelegramStatus(null);
    setTelegramUsername(null);
    setTelegramDisplayName(null);
  }, [linkedProviders]);

  useEffect(() => {
    if (auth.status !== "authenticated") return;

    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastAuthRefreshRef.current < AUTH_REFRESH_MIN_INTERVAL_MS) {
        return;
      }
      lastAuthRefreshRef.current = now;
      void auth.checkAuth();
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    const interval = window.setInterval(refresh, AUTH_REFRESH_INTERVAL_MS);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.clearInterval(interval);
    };
  }, [auth.checkAuth, auth.status]);

  useEffect(() => subscribeAuthFlowComplete(() => {
    void auth.checkAuth();
  }), [auth]);

  useEffect(() => {
    if (!isAuthFlowComplete) return;
    notifyAuthFlowComplete();
  }, [isAuthFlowComplete]);

  useEffect(() => {
    if (auth.status !== "authenticated") return;
    syncAuthenticatedPostHogUser(auth.user);
    attachBrowserAcquisition();
    let cancelled = false;
    void getUserSettings()
      .then(({ preferences }) => {
        if (cancelled) return;
        setPreferences(preferences);
        cachePreferences(preferences);
      })
      .catch(() => {
        if (cancelled) return;
        setPreferences(DEFAULT_PREFERENCES);
      });
    return () => {
      cancelled = true;
    };
  }, [auth.status, auth.status === "authenticated" ? auth.user : null]);

  useEffect(() => {
    if (auth.status !== "authenticated") return;
    captureAuthenticatedPageview(
      postHogRouteName({
        route: currentRoute,
        telegramManagerRoute,
        telegramFilePreviewRoute,
        telegramServerRoute,
        telegramSessionRoute,
        isTelegramMiniAppRoute,
      }),
    );
  }, [
    auth.status,
    currentRoute,
    telegramManagerRoute,
    telegramFilePreviewRoute,
    telegramServerRoute,
    telegramSessionRoute,
    isTelegramMiniAppRoute,
  ]);

  if (isAuthFlowComplete) {
    return <AuthFlowCompletePage />;
  }

  if (telegramFilePreviewRoute) {
    return (
      <AppShellBoundary>
        <Suspense fallback={<PageLoading />}>
          <TelegramFilePreviewMiniApp
            token={telegramFilePreviewRoute.token}
            authStatus={auth.status}
          />
        </Suspense>
      </AppShellBoundary>
    );
  }

  if (
    auth.status === "authenticated" &&
    !auth.user.onboarding_survey_completed &&
    !surveyDeferredForCurrentUser &&
    !isTelegramMiniAppRoute
  ) {
    return (
      <AppShellBoundary>
        <Suspense fallback={<PageLoading />}>
          <OnboardingSurveyPage
            userEmail={auth.user.email}
            onComplete={handleSurveyComplete}
            onDefer={handleSurveyDefer}
          />
        </Suspense>
      </AppShellBoundary>
    );
  }

  if (telegramManagerRoute) {
    return (
      <AppShellBoundary>
        <Suspense fallback={<PageLoading />}>
          <TelegramManagerMiniApp
            authStatus={auth.status}
            user={auth.status === "authenticated" ? auth.user : undefined}
            initialSection={telegramManagerRoute.section}
            startParam={getTelegramWebAppStartParam()}
            onAuthenticated={auth.checkAuth}
          />
        </Suspense>
      </AppShellBoundary>
    );
  }

  if (telegramServerRoute) {
    return (
      <AppShellBoundary>
        <Suspense fallback={<PageLoading />}>
          <TelegramServerPage
            vpsId={telegramServerRoute.vpsId}
            authStatus={auth.status}
          />
        </Suspense>
      </AppShellBoundary>
    );
  }

  if (telegramSessionRoute) {
    return (
      <AppShellBoundary>
        <Suspense fallback={<PageLoading />}>
          <TelegramSessionPage
            sessionId={telegramSessionRoute.sessionId}
            vpsId={telegramSessionRoute.vpsId}
            sessionTitle={telegramSessionRoute.sessionTitle}
            sessionWorkdir={telegramSessionRoute.sessionWorkdir}
            backVpsId={telegramSessionRoute.backVpsId}
            authStatus={auth.status}
          />
        </Suspense>
      </AppShellBoundary>
    );
  }

  if (auth.status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        {auth.connectionUnavailable ? (
          <div className="flex max-w-sm flex-col items-center gap-3 px-6 text-center">
            <div className="text-sm font-medium text-foreground">Can’t reach Chatcode</div>
            <div className="text-sm text-muted-foreground">
              Check your connection. We’ll keep retrying automatically.
            </div>
            <button
              type="button"
              className="rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
              onClick={() => void auth.checkAuth()}
            >
              Retry now
            </button>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Loading...</div>
        )}
      </div>
    );
  }

  if (auth.status === "unauthenticated") {
    return (
      <AppShellBoundary>
        <Suspense fallback={<PageLoading />}>
          <AuthPage />
        </Suspense>
      </AppShellBoundary>
    );
  }

  const userEmail = auth.user.email;
  const controlPlaneVersion = auth.user.control_plane_version ?? null;

  return (
    <AppShellBoundary>
      <Suspense fallback={<PageLoading />}>
        <>
          <AppPage
            userEmail={userEmail}
            controlPlaneVersion={controlPlaneVersion}
            telegramLinked={linkedProviders.includes("telegram")}
            telegramStatus={telegramStatus}
            onLogout={auth.logout}
            onNavigate={handleNavigate}
            overlayOpen={effectiveOverlay !== null}
            externalRefreshSignal={sidebarRefreshSignal}
            selectedVpsIdHint={selectedVpsIdHint}
            routeTarget={routeTarget}
            onForegroundRouteChange={handleForegroundRouteChange}
            onRouteTargetHandled={handleRouteTargetHandled}
          />
          {effectiveOverlay && (
            <div
              ref={overlayRef}
              className="fixed inset-0 z-50 overflow-y-auto overscroll-none bg-background"
              role="dialog"
              aria-modal="true"
              tabIndex={-1}
            >
              {/* Loading any overlay must not hide AppPage: Suspense detaches
                  terminal refs, disposing their sockets and local state. Keep
                  the dialog mounted too so focus stays out of the terminals. */}
              <Suspense fallback={<PageLoading />}>
                {effectiveOverlay === "onboarding" && (
                  <OnboardingPage
                    onBack={handleCloseOverlay}
                    onComplete={handleOnboardingComplete}
                    manualVpsId={onboardingManualVpsId}
                    initialMode={newServerMode}
                    onModeChange={handleNewServerModeChange}
                    onOpenPlanSettings={() => handleNavigate("settings")}
                  />
                )}
                {effectiveOverlay === "settings" && (
                  <SettingsPage
                    userEmail={userEmail}
                    linkedProviders={linkedProviders}
                    onProvidersChanged={setLinkedProviders}
                    telegramStatus={telegramStatus}
                    telegramUsername={telegramUsername}
                    telegramDisplayName={telegramDisplayName}
                    preferences={preferences}
                    onPreferencesChanged={(next) => {
                      setPreferences(next);
                      cachePreferences(next);
                    }}
                    focusVpsId={settingsFocusVpsId}
                    section={settingsSection}
                    onSectionChange={handleSettingsSectionChange}
                    onServersChanged={() => setSidebarRefreshSignal((value) => value + 1)}
                    onBack={handleCloseOverlay}
                    onLogout={auth.logout}
                  />
                )}
              </Suspense>
            </div>
          )}
        </>
      </Suspense>
    </AppShellBoundary>
  );
}

function getTelegramSessionRoute(location: Location): {
  sessionId: string;
  vpsId: string;
  sessionTitle: string;
  sessionWorkdir: string;
  backVpsId: string | null;
} | null {
  const match = location.pathname.match(/^\/tg\/session\/([^/]+)$/);
  if (!match) {
    return null;
  }

  const params = new URLSearchParams(location.search);
  return {
    sessionId: decodeURIComponent(match[1]),
    vpsId: params.get("vps_id")?.trim() || "",
    sessionTitle: params.get("title")?.trim() || decodeURIComponent(match[1]),
    sessionWorkdir: params.get("workdir")?.trim() || ".",
    backVpsId: params.get("back_vps_id")?.trim() || null,
  };
}

function postHogRouteName(input: {
  route: AppRoute;
  telegramManagerRoute: { section: "home" | "top" | "partner" | "faq" | "settings" } | null;
  telegramFilePreviewRoute: { token: string | null } | null;
  telegramServerRoute: { vpsId: string } | null;
  telegramSessionRoute: {
    sessionId: string;
    vpsId: string;
    sessionTitle: string;
    sessionWorkdir: string;
    backVpsId: string | null;
  } | null;
  isTelegramMiniAppRoute: boolean;
}): string {
  if (input.telegramFilePreviewRoute) return "telegram_preview";
  if (input.telegramSessionRoute) return "telegram_session";
  if (input.telegramServerRoute) return "telegram_server";
  if (input.telegramManagerRoute) return `telegram_${input.telegramManagerRoute.section}`;
  if (input.isTelegramMiniAppRoute) return "telegram_unknown";
  switch (input.route.kind) {
    case "root":
      return "app_home";
    case "newServer":
      return `new_server_${input.route.mode}`;
    case "settings":
      return `settings_${input.route.section}`;
    case "vps":
      return "server";
    case "vpsSettings":
      return "server_settings";
    case "session":
      return "session";
  }
}

function getTelegramFilePreviewRoute(location: Location): { token: string | null } | null {
  const dedicatedPreviewRoute = /^\/tg\/preview\/?$/.test(location.pathname);
  const legacyManagerStartParamRoute = isTelegramManagerRoute(location);
  if (!dedicatedPreviewRoute && !legacyManagerStartParamRoute) return null;

  const startParam = getTelegramWebAppStartParam();
  if (startParam.startsWith("file_")) {
    return { token: startParam };
  }

  return dedicatedPreviewRoute ? { token: null } : null;
}

function getTelegramManagerRoute(location: Location): { section: "home" | "top" | "partner" | "faq" | "settings" } | null {
  const match = location.pathname.match(/^\/tg(?:\/(top|partner|faq|settings))?\/?$/);
  if (!match) return null;
  const section = match[1];
  if (section === "top" || section === "partner" || section === "faq" || section === "settings") {
    return { section };
  }
  return { section: "home" };
}

function isTelegramManagerRoute(location: Location): boolean {
  return getTelegramManagerRoute(location) !== null;
}

function getTelegramServerRoute(location: Location): {
  vpsId: string;
} | null {
  const match = location.pathname.match(/^\/tg\/server\/([^/]+)$/);
  if (!match) {
    return null;
  }
  return {
    vpsId: decodeURIComponent(match[1]),
  };
}

function PageLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-sm text-muted-foreground">Loading...</div>
    </div>
  );
}

function TelegramFilePreviewMiniApp({
  token,
  authStatus,
}: {
  token: string | null;
  authStatus: "loading" | "unauthenticated" | "authenticated";
}) {
  const { initData: telegramInitData, waiting: telegramInitDataWaiting } = useTelegramWebAppInitData();
  const [state, setState] = useState<"loading" | "ready" | "unlinked" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    webApp?.ready?.();
    webApp?.expand?.();
    webApp?.disableVerticalSwipes?.();
    return () => {
      webApp?.enableVerticalSwipes?.();
      webApp?.BackButton?.hide?.();
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("Invalid preview link.");
      return;
    }
    if (authStatus === "loading" || (authStatus === "unauthenticated" && !telegramInitData && telegramInitDataWaiting)) {
      setState("loading");
      setMessage(telegramInitDataWaiting ? "Waiting for Telegram launch data…" : "");
      return;
    }
    if (authStatus === "authenticated") {
      setState("ready");
      setMessage("");
      return;
    }
    if (!telegramInitData) {
      setState("error");
      setMessage("Open this file preview from the Telegram bot.");
      return;
    }

    let cancelled = false;
    setState("loading");
    setMessage("");
    void establishMiniAppAuthSession(telegramInitData).then((result) => {
      if (cancelled) return;
      if (result.ok) {
        setState("ready");
        setMessage("");
        return;
      }
      setState(result.state);
      setMessage(result.message);
    });
    return () => {
      cancelled = true;
    };
  }, [authStatus, telegramInitData, token]);

  if (state !== "ready" || !token) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-3">
          <div className="text-sm font-medium">
            {state === "loading" ? "Opening file…" : state === "unlinked" ? "Telegram not linked" : "File unavailable"}
          </div>
          <div className="text-sm text-muted-foreground">
            {message || "Preparing Telegram file preview."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <TelegramFilePreviewPane token={token} />
  );
}

function TelegramManagerMiniApp({
  authStatus,
  user,
  initialSection,
  startParam,
  onAuthenticated,
}: {
  authStatus: "loading" | "unauthenticated" | "authenticated";
  user?: User;
  initialSection: "home" | "top" | "partner" | "faq" | "settings";
  startParam: string;
  onAuthenticated: () => Promise<void>;
}) {
  const { initData: telegramInitData } = useTelegramWebAppInitData();
  const [authAttempt, setAuthAttempt] = useState<"idle" | "authenticating" | "attempted" | "failed">("idle");
  // The manager Mini App has a useful public home for unlinked users, so auth
  // probes must not hold the route on the loading shell.
  const authStatusForMiniApp =
    authStatus === "loading" && telegramInitData ? "unauthenticated" : authStatus;

  useEffect(() => {
    if (authStatusForMiniApp !== "unauthenticated") {
      return;
    }
    if (!telegramInitData || authAttempt !== "idle") {
      return;
    }

    let cancelled = false;
    setAuthAttempt("authenticating");
    void establishMiniAppAuthSession(telegramInitData)
      .then(async (result) => {
        if (cancelled) return;
        if (!result.ok) {
          setAuthAttempt("failed");
          return;
        }
        await onAuthenticated();
        if (!cancelled) {
          setAuthAttempt("attempted");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAuthAttempt("failed");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authAttempt, authStatusForMiniApp, onAuthenticated, telegramInitData]);

  return (
    <TelegramManagerPage
      authStatus={authStatusForMiniApp}
      user={user}
      initialSection={initialSection}
      startParam={startParam}
    />
  );
}

function AuthFlowCompletePage() {
  const [closeBlocked, setCloseBlocked] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      window.close();
      window.setTimeout(() => {
        if (!window.closed) {
          setCloseBlocked(true);
        }
      }, 150);
    }, 50);

    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-sm rounded-lg border border-border bg-card px-6 py-5">
        <h1 className="text-base font-semibold text-foreground">
          {closeBlocked ? "Authentication complete" : "Closing tab..."}
        </h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {closeBlocked
            ? "The sign-in flow is complete. This tab did not close automatically, so you can close it and return to the app."
            : "The sign-in flow is complete. This tab should close automatically."}
        </p>
        {closeBlocked ? (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => window.close()}
              className="inline-flex items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors"
            >
              Close tab
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

interface AppShellBoundaryProps {
  children: ReactNode;
}

interface AppShellBoundaryState {
  error: Error | null;
  reloading: boolean;
}

class AppShellBoundary extends Component<AppShellBoundaryProps, AppShellBoundaryState> {
  state: AppShellBoundaryState = {
    error: null,
    reloading: false,
  };

  static getDerivedStateFromError(error: Error): AppShellBoundaryState {
    return { error, reloading: false };
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo) {
    if (!isRecoverableChunkLoadError(error)) return;
    if (!shouldAutoReloadForChunkError()) return;

    this.setState({ error, reloading: true });
    window.setTimeout(() => {
      // Navigate with a cache-bust param so the browser fetches a fresh index.html
      // even if the CDN or browser cache still holds the old one.
      const url = new URL(window.location.href);
      url.searchParams.set("_cb", String(Date.now()));
      window.location.replace(url.toString());
    }, 50);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    if (this.state.reloading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-sm rounded-lg border border-border bg-card px-6 py-5 text-center">
            <h1 className="text-base font-semibold text-foreground">Refreshing app…</h1>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              chatcode is reloading to recover from an outdated tab after a deploy.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-sm rounded-lg border border-border bg-card px-6 py-5">
          <h1 className="text-base font-semibold text-foreground">Reload app</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            This tab is out of date and could not recover automatically. Reload the app to fetch the latest version.
          </p>
          <button
            type="button"
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.set("_cb", String(Date.now()));
              window.location.replace(url.toString());
            }}
            className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Reload app
          </button>
        </div>
      </div>
    );
  }
}
